import { Box, useTheme } from '@mui/material'
import DragSelectMap from '@components/map/DragSelectMap'
import NotificationContainer from '../components/Notifications/features/NotificationContainer'
import StatCard from '../components/StatCard'
import { getWaterMeterStats, WaterMeterRow, getAccountDashboard, AccountDashboard } from '@/services/dashboardService'
import { getConsumers } from '@/services/consumerService'
// import WaterConsumptionChart from '../components/WaterConsumptionChart/WaterConsumptionChart'
import Header from '@components/heder/Header'
import YearComparisonChart from '@/components/charts/YearComparisonChart'
import { useNavigate, useParams } from 'react-router-dom'
import { useTableStore } from '@/store/table/table'
import { getDashboardStats } from '@/local_db/db'
import { useState, useEffect, useMemo } from 'react'
import { DashboardStats } from '@/local_db/db'
import { transformWaterToChartPoints } from '@/utils/chartUtils'
import { debugLog } from '@/utils/debugLog'

export default function Dashboard() {
  const theme = useTheme()
  const navigate = useNavigate()
  const { siteId } = useParams()
  const { setFilteredRowsStore } = useTableStore()
  const [, setDashboardStats] = useState<DashboardStats[]>([])
  useEffect(() => {
    const fetchDashboardStats = async () => {
      const data = await getDashboardStats()
      setDashboardStats(data)
    }
    fetchDashboardStats()
  }, [])
  const executeSearch = (value: string) => {
    if (!value.trim()) {
      setFilteredRowsStore({})
      return
    }
    const searchValue = value.trim().toLowerCase()
    setFilteredRowsStore({ searchFilter: { label: 'חיפוש', value: searchValue } })
    navigate(`/${siteId}/consumers?search=${encodeURIComponent(searchValue)}`)
  }
  const [waterStats, setWaterStats] = useState<WaterMeterRow[]>([])
  const [isChartLoading, setIsChartLoading] = useState(true)
  const [chartError, setChartError] = useState<string | null>(null)
  
  useEffect(() => {
    const fetchWater = async () => {
      // Wait for token to be available
      const token = localStorage.getItem('authToken')
      if (!token) {
        debugLog('No auth token yet, skipping fetch')
        return
      }
      
      setIsChartLoading(true)
      setChartError(null)
      try {
        const rows = await getWaterMeterStats() 
        setWaterStats(rows)
      } catch (error) {
        console.error('Failed to fetch water stats:', error)
        setChartError('שגיאה בטעינת הנתונים')
      } finally {
        setIsChartLoading(false)
      }
    }
    fetchWater()
  }, [])

  // Account Dashboard Stats (for StatCards)
  const [accountStats, setAccountStats] = useState<AccountDashboard | null>(null)
  const [totalEndUnits, setTotalEndUnits] = useState<number | null>(null)

  useEffect(() => {
    const fetchAccountStats = async () => {
      // Wait for token to be available
      const token = localStorage.getItem('authToken')
      if (!token) return

      try {
        const data = await getAccountDashboard()
        setAccountStats(data)
      } catch (error) {
        console.error('Failed to fetch account stats:', error)
      }
    }
    fetchAccountStats()
  }, [])

  // Fetch total end-units count from consumers API
  useEffect(() => {
    const fetchTotalEndUnits = async () => {
      const token = localStorage.getItem('authToken')
      const accountId = localStorage.getItem('accountId')
      if (!token || !accountId) return
      try {
        const result = await getConsumers({ accountId: Number(accountId), page: 1, size: 1 })
        setTotalEndUnits(result.total)
      } catch (error) {
        console.error('Failed to fetch total end units:', error)
      }
    }
    fetchTotalEndUnits()
  }, [])

  // Transform water data to canonical chart format
  const chartData = useMemo(() => {
    if (waterStats.length === 0) return []
    const points = transformWaterToChartPoints(waterStats)
    
    // Temporary logging for verification
    debugLog('[Dashboard] First date:', points[0]?.x)
    debugLog('[Dashboard] Last date:', points[points.length - 1]?.x)
    debugLog('[Dashboard] Total points:', points.length)
    
    return points
  }, [waterStats])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        paddingLeft: theme.spacing(2),
        marginRight: theme.spacing(2),
        backgroundColor: theme.palette.background.default,
        height: '100%',
        overflow: 'auto',
      }}
    >
      <Header executeSearch={executeSearch} />
      <Box sx={{ display: 'flex', gap: theme.spacing(5), flexWrap: 'wrap', flex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: theme.spacing(1),
              justifyContent: 'space-between',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: theme.spacing(2),
                justifyContent: 'center',
              }}
            >
              {/*<StatCard
                title="אחוזי קליטה"
                value={accountStats ? `${accountStats.activePercentage}%` : '-'}
                height={105}
                width={164}
              />*/}
              <StatCard
                onClick={() => {
                  navigate(`/${siteId}/consumers`)
                }}
                title="יחידות קצה"
                value={totalEndUnits ?? accountStats?.armCount ?? '-'}
                height={105}
                width={164}
              />
            </Box>
            {/*<Box
              sx={{
                display: 'flex',
                gap: theme.spacing(2),
                justifyContent: 'center',
              }}
            >
              <StatCard
                onClick={() => {
                  navigate(`/${siteId}/crm`)
                }}
                title="משימות פתוחות"
                value={accountStats?.openTasks ?? 0}
                height={105}
                width={164}
              />
              <StatCard
                onClick={() => {
                  navigate(`/${siteId}/crm`)
                }}
                title="משימות בביצוע"
                value={accountStats?.inProgressTasks ?? 0}
                height={105}
                width={164}
              />
            </Box>*/}
          </Box>
          <Box sx={{ flex: 1, minHeight: 300, display: 'flex', flexDirection: 'column' }}>
            <NotificationContainer height="100%" maxHeight="none" />
          </Box>
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
            mb: 1,
          }}
        >
     {chartError ? (
        <Box
          sx={{
            height: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: theme.palette.mode === 'dark' ? '#202B40' : '#FAFBFD',
            borderRadius: 2,
            border: `1px solid ${theme.palette.mode === 'dark' ? '#374151' : '#E6ECF2'}`,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Box sx={{ fontSize: 32 }}>⚠️</Box>
            <Box sx={{ color: theme.palette.error.main, fontSize: 14, fontWeight: 500 }}>{chartError}</Box>
          </Box>
        </Box>
      ) : (isChartLoading || chartData.length > 0) ? (
        <YearComparisonChart
          legend={true}
          showCompareLabel={false}
          headerTitle="נתוני צריכת מים"
          height={200}
          loading={isChartLoading}
          data={chartData}
        />
      ) : null}
          <DragSelectMap interaction="view" />
        </Box>
      </Box>
    </Box>
  )
}
